import { useMemo, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import useAutoRefresh from "@/hooks/useAutoRefresh";
import api from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Printer, Package, Buildings, ArrowUpRight, ArrowDown, ArrowClockwise,
  Calendar, Factory,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const ALLOWED_ROLES = ["admin", "owner", "admin_kantor"];

const fmt = (n) => (n ?? 0).toLocaleString("id-ID", { maximumFractionDigits: 0 });
const fmtDate = (iso) => new Date(iso).toLocaleDateString("id-ID", { day: "numeric", month: "short" });

// Quick date presets
function computePreset(preset) {
  const t = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  const start = new Date(t);
  if (preset === "today")        return { from: iso(t), to: iso(t) };
  if (preset === "week")         { start.setDate(t.getDate() - 6); return { from: iso(start), to: iso(t) }; }
  if (preset === "month")        { start.setDate(1);               return { from: iso(start), to: iso(t) }; }
  if (preset === "last30")       { start.setDate(t.getDate() - 29); return { from: iso(start), to: iso(t) }; }
  if (preset === "prevmonth")    {
    const s = new Date(t.getFullYear(), t.getMonth() - 1, 1);
    const e = new Date(t.getFullYear(), t.getMonth(), 0);
    return { from: iso(s), to: iso(e) };
  }
  return { from: iso(t), to: iso(t) };
}

export default function PeriodReportPage() {
  const { user } = useAuth();
  const [{ from, to }, setRange] = useState(() => computePreset("month"));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!from || !to) return;
    setLoading(true);
    try {
      const r = await api.get(`/reports/period`, { params: { from_date: from, to_date: to } });
      setData(r.data);
    } catch (e) {
      toast.error("Gagal memuat laporan periode");
    } finally { setLoading(false); }
  };

  useAutoRefresh(load, { deps: [from, to], interval: 0 });

  // net = incoming - outgoing (visualisasi neraca) — HARUS dipanggil sebelum early return
  const net = useMemo(() => {
    if (!data) return 0;
    return (data.incoming?.receiving?.total_liter || 0) + (data.incoming?.retur?.total_liter || 0) - (data.outgoing?.delivery?.total_liter || 0);
  }, [data]);

  // Max per-day untuk normalisasi grafik bar
  const maxDay = useMemo(() => {
    if (!data?.by_day?.length) return 1;
    return Math.max(1, ...data.by_day.map((d) => Math.max(d.incoming_liter || 0, d.outgoing_liter || 0, d.retur_liter || 0)));
  }, [data]);

  // Guard: only admin/owner/admin_kantor
  if (user && !ALLOWED_ROLES.includes(user.role)) return <Navigate to="/" replace />;

  const applyPreset = (p) => setRange(computePreset(p));

  const printReport = () => {
    if (!data) return;
    const rangeLabel = `${new Date(from).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })} — ${new Date(to).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })}`;
    const w = window.open("", "_blank", "width=900,height=1000");
    if (!w) return;
    const tRows = (arr, cols) => arr.map((r) => `<tr>${cols.map((c) => `<td>${c(r)}</td>`).join("")}</tr>`).join("");
    w.document.write(`<!DOCTYPE html><html><head><title>Laporan Periode ${from} — ${to}</title>
      <style>
        body{font-family:sans-serif;padding:32px;color:#0f172a;font-size:12px}
        h1{font-size:22px;margin:0 0 6px}
        h2{font-size:14px;margin:24px 0 8px;border-bottom:2px solid #0ea5e9;padding-bottom:4px;text-transform:uppercase;letter-spacing:0.05em}
        .header{display:flex;align-items:center;gap:12px;border-bottom:2px solid #0f172a;padding-bottom:12px}
        .cards{display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin:16px 0}
        .card{border:1px solid #e2e8f0;padding:10px;border-radius:6px;background:#f8fafc}
        .card .lbl{font-size:9px;text-transform:uppercase;letter-spacing:0.08em;color:#64748b}
        .card .val{font-size:22px;font-weight:900;color:#0f172a;margin-top:2px}
        table{width:100%;border-collapse:collapse;font-size:11px;margin-top:4px}
        th{text-align:left;padding:6px;background:#f1f5f9;font-size:9px;text-transform:uppercase;letter-spacing:0.06em;color:#475569}
        td{padding:6px;border-bottom:1px solid #f1f5f9}
        .mono{font-family:monospace}
        .empty{color:#94a3b8;font-style:italic;padding:12px;text-align:center}
        @media print{body{padding:16px}}
      </style></head><body>
      <div class="header">
        <div>
          <div style="font-weight:900;font-size:16px">PT. PRIMERGY SOLUTION</div>
          <div style="font-size:11px;color:#64748b">Laporan Bahan Baku Masuk vs Barang Keluar</div>
        </div>
        <div style="margin-left:auto;text-align:right">
          <div style="font-size:10px;text-transform:uppercase;letter-spacing:0.08em;color:#64748b">Periode</div>
          <div style="font-weight:800;font-size:14px">${rangeLabel}</div>
        </div>
      </div>

      <h2>Ringkasan</h2>
      <div class="cards">
        <div class="card"><div class="lbl">Bahan Baku Masuk (L)</div><div class="val">${fmt(data.incoming.receiving.total_liter)}</div></div>
        <div class="card"><div class="lbl">Retur Masuk (L)</div><div class="val">${fmt(data.incoming.retur.total_liter)}</div></div>
        <div class="card"><div class="lbl">Barang Keluar (L)</div><div class="val">${fmt(data.outgoing.delivery.total_liter)}</div></div>
        <div class="card"><div class="lbl">Net (L)</div><div class="val">${fmt(net)}</div></div>
        <div class="card"><div class="lbl">Receiving Docs</div><div class="val">${data.incoming.receiving.count}</div></div>
        <div class="card"><div class="lbl">Retur Docs</div><div class="val">${data.incoming.retur.count}</div></div>
        <div class="card"><div class="lbl">Delivery Docs</div><div class="val">${data.outgoing.delivery.count}</div></div>
        <div class="card"><div class="lbl">Total Hari</div><div class="val">${data.range.days}</div></div>
      </div>

      <h2>Bahan Baku Masuk — per Material</h2>
      ${data.incoming.by_material.length === 0 ? '<div class="empty">Tidak ada data.</div>' :
        `<table><thead><tr><th>Material</th><th style="text-align:right">Dokumen</th><th style="text-align:right">Qty (L)</th></tr></thead><tbody>${
          tRows(data.incoming.by_material, [
            (r)=>r.key, (r)=>`<span class="mono" style="float:right">${r.count}</span>`, (r)=>`<span class="mono" style="float:right">${fmt(r.qty)}</span>`,
          ])}</tbody></table>`}

      <h2>Bahan Baku Masuk — per Supplier (Top 15)</h2>
      ${data.incoming.by_supplier.length === 0 ? '<div class="empty">Tidak ada data.</div>' :
        `<table><thead><tr><th>Supplier</th><th style="text-align:right">Dokumen</th><th style="text-align:right">Qty (L)</th></tr></thead><tbody>${
          tRows(data.incoming.by_supplier, [
            (r)=>r.key, (r)=>`<span class="mono" style="float:right">${r.count}</span>`, (r)=>`<span class="mono" style="float:right">${fmt(r.qty)}</span>`,
          ])}</tbody></table>`}

      <h2>Barang Keluar — per Produk</h2>
      ${data.outgoing.by_product.length === 0 ? '<div class="empty">Tidak ada data.</div>' :
        `<table><thead><tr><th>Produk</th><th style="text-align:right">Dokumen</th><th style="text-align:right">Qty (L)</th></tr></thead><tbody>${
          tRows(data.outgoing.by_product, [
            (r)=>r.key, (r)=>`<span class="mono" style="float:right">${r.count}</span>`, (r)=>`<span class="mono" style="float:right">${fmt(r.qty)}</span>`,
          ])}</tbody></table>`}

      <h2>Barang Keluar — per Customer (Top 15)</h2>
      ${data.outgoing.by_customer.length === 0 ? '<div class="empty">Tidak ada data.</div>' :
        `<table><thead><tr><th>Customer</th><th style="text-align:right">Dokumen</th><th style="text-align:right">Qty (L)</th></tr></thead><tbody>${
          tRows(data.outgoing.by_customer, [
            (r)=>r.key, (r)=>`<span class="mono" style="float:right">${r.count}</span>`, (r)=>`<span class="mono" style="float:right">${fmt(r.qty)}</span>`,
          ])}</tbody></table>`}

      <div style="margin-top:32px;font-size:10px;color:#94a3b8;text-align:right">
        Dicetak ${new Date().toLocaleString("id-ID")}
      </div>
    </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 400);
  };

  return (
    <AppShell
      title="Laporan Periodik"
      subtitle="Ringkasan bahan baku masuk vs barang keluar dalam rentang waktu tertentu"
      actions={
        <Button
          onClick={printReport}
          disabled={!data}
          data-testid="period-print-btn"
          className="bg-slate-900 hover:bg-slate-800 h-10 gap-2 text-white"
        >
          <Printer size={16} weight="duotone" /> <span className="hidden sm:inline">Cetak / Simpan PDF</span>
        </Button>
      }
    >
      {/* Filter periode */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm mb-6">
        <div className="flex flex-col lg:flex-row lg:items-end gap-4">
          <div className="flex-1 grid grid-cols-2 gap-4 max-w-md">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500 flex items-center gap-1.5">
                <Calendar size={11} weight="duotone" /> Dari Tanggal
              </label>
              <Input
                type="date"
                value={from}
                onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
                data-testid="period-from"
                className="mt-1.5 h-10 font-mono"
              />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500 flex items-center gap-1.5">
                <Calendar size={11} weight="duotone" /> Sampai Tanggal
              </label>
              <Input
                type="date"
                value={to}
                onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
                data-testid="period-to"
                className="mt-1.5 h-10 font-mono"
              />
            </div>
          </div>

          {/* Presets */}
          <div className="flex flex-wrap gap-2">
            {[
              { k: "today",     l: "Hari Ini" },
              { k: "week",      l: "7 Hari Terakhir" },
              { k: "month",     l: "Bulan Ini" },
              { k: "prevmonth", l: "Bulan Lalu" },
              { k: "last30",    l: "30 Hari Terakhir" },
            ].map((p) => (
              <button
                key={p.k}
                onClick={() => applyPreset(p.k)}
                data-testid={`period-preset-${p.k}`}
                className="text-[11px] font-semibold px-3 py-1.5 rounded-md border border-slate-200 hover:bg-sky-50 hover:border-sky-300 text-slate-700 transition-colors"
              >
                {p.l}
              </button>
            ))}
            <button
              onClick={load}
              disabled={loading}
              data-testid="period-refresh"
              className="text-[11px] font-bold px-3 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-800 flex items-center gap-1"
            >
              <ArrowClockwise size={12} weight="bold" className={loading ? "animate-spin" : ""} />
              {loading ? "Memuat…" : "Muat Ulang"}
            </button>
          </div>
        </div>
      </div>

      {!data ? (
        <div className="rounded-xl bg-white border border-slate-200 py-14 text-center text-slate-400 text-sm">
          {loading ? "Memuat data…" : "Pilih rentang tanggal untuk menampilkan laporan."}
        </div>
      ) : (
        <>
          {/* Ringkasan cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="period-summary">
            <SummaryCard
              label="Bahan Baku Masuk"
              value={data.incoming.receiving.total_liter}
              unit="L"
              count={data.incoming.receiving.count}
              icon={ArrowDown}
              tone="sky"
              testid="sum-incoming"
            />
            <SummaryCard
              label="Retur Masuk"
              value={data.incoming.retur.total_liter}
              unit="L"
              count={data.incoming.retur.count}
              icon={ArrowClockwise}
              tone="orange"
              testid="sum-retur"
            />
            <SummaryCard
              label="Barang Keluar"
              value={data.outgoing.delivery.total_liter}
              unit="L"
              count={data.outgoing.delivery.count}
              icon={ArrowUpRight}
              tone="amber"
              testid="sum-outgoing"
            />
            <SummaryCard
              label="Net (Masuk − Keluar)"
              value={net}
              unit="L"
              icon={Factory}
              tone={net >= 0 ? "emerald" : "red"}
              testid="sum-net"
            />
          </div>

          {/* Grafik per-hari */}
          <div className="rounded-xl bg-white border border-slate-200 p-5 shadow-sm mb-6" data-testid="period-chart">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-800">Pergerakan Harian</h3>
              <div className="flex items-center gap-3 text-[10px]">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-sky-500" /> Masuk</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-orange-400" /> Retur</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-amber-500" /> Keluar</span>
              </div>
            </div>
            {data.by_day.length === 0 ? (
              <div className="py-8 text-center text-xs text-slate-400">Tidak ada aktivitas pada rentang ini.</div>
            ) : (
              <div className="overflow-x-auto">
                <div className="flex items-end gap-2 min-h-[180px] pb-2" style={{ minWidth: `${data.by_day.length * 48}px` }}>
                  {data.by_day.map((d) => {
                    const inH  = ((d.incoming_liter || 0) / maxDay) * 160;
                    const reH  = ((d.retur_liter || 0)    / maxDay) * 160;
                    const outH = ((d.outgoing_liter || 0) / maxDay) * 160;
                    return (
                      <div key={d.date} className="flex flex-col items-center gap-1 shrink-0 w-10" title={`${d.date}\nMasuk: ${fmt(d.incoming_liter)} L\nRetur: ${fmt(d.retur_liter)} L\nKeluar: ${fmt(d.outgoing_liter)} L`}>
                        <div className="flex items-end gap-0.5 h-[160px]">
                          <div className="w-2.5 rounded-t-sm bg-sky-500" style={{ height: `${inH}px` }} />
                          <div className="w-2.5 rounded-t-sm bg-orange-400" style={{ height: `${reH}px` }} />
                          <div className="w-2.5 rounded-t-sm bg-amber-500" style={{ height: `${outH}px` }} />
                        </div>
                        <div className="text-[9px] text-slate-500 font-mono">{fmtDate(d.date)}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Breakdown tables */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <BreakdownPanel
              title="Bahan Baku Masuk — per Material"
              icon={Package}
              tone="sky"
              rows={data.incoming.by_material}
              testid="bd-material"
              emptyMsg="Tidak ada penerimaan pada periode ini."
            />
            <BreakdownPanel
              title="Bahan Baku Masuk — per Supplier"
              icon={Buildings}
              tone="sky"
              rows={data.incoming.by_supplier}
              testid="bd-supplier"
              emptyMsg="Tidak ada supplier tercatat."
            />
            <BreakdownPanel
              title="Barang Keluar — per Produk"
              icon={Package}
              tone="amber"
              rows={data.outgoing.by_product}
              testid="bd-product"
              emptyMsg="Tidak ada barang keluar pada periode ini."
            />
            <BreakdownPanel
              title="Barang Keluar — per Customer"
              icon={Buildings}
              tone="amber"
              rows={data.outgoing.by_customer}
              testid="bd-customer"
              emptyMsg="Tidak ada customer tercatat."
            />
            {data.incoming.by_retur_source.length > 0 && (
              <BreakdownPanel
                title="Sumber Retur"
                icon={ArrowClockwise}
                tone="orange"
                rows={data.incoming.by_retur_source}
                testid="bd-retur"
                emptyMsg="Tidak ada retur pada periode ini."
              />
            )}
          </div>
        </>
      )}
    </AppShell>
  );
}

const TONE_MAP = {
  sky:     { bg: "bg-sky-50 border-sky-200",       text: "text-sky-900",     val: "text-sky-800",     ic: "text-sky-600" },
  orange:  { bg: "bg-orange-50 border-orange-200", text: "text-orange-900",  val: "text-orange-800",  ic: "text-orange-600" },
  amber:   { bg: "bg-amber-50 border-amber-200",   text: "text-amber-900",   val: "text-amber-800",   ic: "text-amber-600" },
  emerald: { bg: "bg-emerald-50 border-emerald-200", text: "text-emerald-900", val: "text-emerald-800", ic: "text-emerald-600" },
  red:     { bg: "bg-red-50 border-red-200",       text: "text-red-900",     val: "text-red-800",     ic: "text-red-600" },
};

function SummaryCard({ label, value, unit, count, icon: Icon, tone = "sky", testid }) {
  const t = TONE_MAP[tone] || TONE_MAP.sky;
  return (
    <div className={`rounded-xl border ${t.bg} p-4 shadow-sm relative overflow-hidden`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className={`text-[10px] font-bold uppercase tracking-[0.14em] ${t.text} opacity-70`}>{label}</div>
        <Icon size={20} weight="duotone" className={t.ic} />
      </div>
      <div className={`font-heading font-black text-3xl lg:text-4xl mt-3 leading-none ${t.val}`}>
        {fmt(value)} <span className="text-base font-bold opacity-60">{unit}</span>
      </div>
      {count !== undefined && (
        <div className={`text-[11px] mt-2 ${t.text} opacity-70`}>{count} dokumen</div>
      )}
    </div>
  );
}

function BreakdownPanel({ title, icon: Icon, tone, rows, testid, emptyMsg }) {
  const t = TONE_MAP[tone] || TONE_MAP.sky;
  const total = rows.reduce((s, r) => s + (r.qty || 0), 0);
  return (
    <div className="rounded-xl bg-white border border-slate-200 shadow-sm overflow-hidden" data-testid={testid}>
      <div className={`px-5 py-3 border-b border-slate-100 flex items-center gap-2 ${t.bg}`}>
        <Icon size={16} weight="duotone" className={t.ic} />
        <h3 className={`font-heading text-sm font-bold uppercase tracking-wider ${t.text}`}>{title}</h3>
        <span className={`ml-auto text-[10px] font-mono ${t.text} opacity-70`}>{rows.length} entri</span>
      </div>
      <div className="divide-y divide-slate-100 max-h-[360px] overflow-y-auto">
        {rows.length === 0 ? (
          <div className="px-5 py-8 text-center text-xs text-slate-400">{emptyMsg}</div>
        ) : (
          rows.map((r, idx) => {
            const pct = total > 0 ? (r.qty / total) * 100 : 0;
            return (
              <div key={`${r.key}-${idx}`} className="px-5 py-2.5 hover:bg-slate-50/70">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-semibold text-slate-800 truncate">{r.key}</div>
                    <div className="text-[10px] text-slate-500">{r.count} dokumen</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[13px] font-mono font-bold text-slate-800">{fmt(r.qty)} L</div>
                    <div className="text-[10px] text-slate-500">{pct.toFixed(1)}%</div>
                  </div>
                </div>
                <div className="mt-1.5 h-1 rounded-full bg-slate-100 overflow-hidden">
                  <div className={`h-full ${tone === "sky" ? "bg-sky-500" : tone === "orange" ? "bg-orange-400" : tone === "amber" ? "bg-amber-500" : "bg-slate-500"}`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
